import { Tooltip } from '@highlight/ui'
import { cn } from '../../utils'
import { ComponentProps, forwardRef, memo } from 'react'

interface CircleButtonProps extends ComponentProps<'button'> {
  disabled?: boolean
  tooltip?: string | React.ReactElement
  tooltipPosition?: 'top' | 'bottom' | 'left' | 'right'
}

export const CircleButton = memo(
  forwardRef<HTMLButtonElement, React.PropsWithChildren<CircleButtonProps>>(
    ({ children, id, className, disabled, tooltip, tooltipPosition, ...props }, ref) => {
      const Button = (
        <button
          ref={ref}
          id={id}
          className={cn(
            'hover:bg-light-10 text-light flex min-h-[28px] min-w-[28px] cursor-pointer items-center justify-center rounded-[50%] bg-transparent opacity-50 transition-[color_opacity] hover:opacity-100',
            className,
            disabled ? 'pointer-events-none opacity-35' : '',
          )}
          {...props}
        >
          {children}
        </button>
      )
      if (tooltip) {
        return (
          <Tooltip>
            {/* A wrapper, not the button: a disabled button is pointer-events-none, so it would never open. */}
            <Tooltip.Trigger render={<div className="relative flex" />}>{Button}</Tooltip.Trigger>
            <Tooltip.Content side={tooltipPosition ?? 'top'}>{tooltip}</Tooltip.Content>
          </Tooltip>
        )
      }
      return Button
    },
  ),
)
CircleButton.displayName = 'CircleButton'

type RoundedButtonProps = React.HTMLAttributes<HTMLDivElement> & {
  disabled?: boolean
}

export const RoundedButton = memo(
  forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement> & React.PropsWithChildren<RoundedButtonProps>>(
    ({ children, id, className, disabled, ...props }, ref) => {
      return (
        <div
          ref={ref}
          id={id}
          className={cn(
            'hover:bg-light-10 text-light flex min-h-[28px] min-w-[28px] cursor-pointer items-center justify-center rounded-lg opacity-50 transition-[color_opacity] hover:opacity-100',
            className,
            disabled ? 'pointer-events-none opacity-35' : '',
          )}
          {...props}
        >
          {children}
        </div>
      )
    },
  ),
)
RoundedButton.displayName = 'RoundedButton'
